import { useEffect, useId } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { MdShield, MdVisibility, MdVisibilityOff, MdWarningAmber } from 'react-icons/md';
import {
    type ActionDiagnosticReference,
    useDocumentSupportDiagnostics,
} from '@/context/DocumentSupportDiagnosticsContext';
import {
    getActionDiagnosticReferences,
    hasPassedAccessibility,
    type IActionDiagnosticTarget,
    useActionDiagnosticDecisions,
} from './useActionDiagnosticDecisions';
import './ActionDiagnosticIndicators.scss';

type IndicatorStage = 'visibility' | 'accessibility';

/**
 * Индикаторы диагностики поддержки у действия: видимость и доступность, нажатие открывает разбор решения. Индикаторы —
 * кнопки, поэтому внутри кнопки действия не ставятся: в кнопке — `ActionDiagnosticSlot` с теми же пропсами, сами
 * индикаторы — рядом, поверх него (`extras` у `ActionKeyGroup`, как ключ кнопки `ActionKeySlot`); у значка без места
 * под них — соседом после него.
 */
const ActionDiagnosticIndicators = (props: IActionDiagnosticTarget) => {
    const { enabled, selectAction, registerMountedActions, unregisterMountedActions } = useDocumentSupportDiagnostics();
    const requestedReferences = getActionDiagnosticReferences(props);
    const decisions = useActionDiagnosticDecisions(requestedReferences);
    const registrationId = useId();
    const registrationPayload = JSON.stringify(requestedReferences);

    useEffect(() => {
        if (!enabled || requestedReferences.length === 0) return undefined;

        const registeredReferences = JSON.parse(registrationPayload) as ActionDiagnosticReference[];
        registerMountedActions(registrationId, registeredReferences);
        return () => unregisterMountedActions(registrationId);
    }, [
        enabled,
        registerMountedActions,
        registrationId,
        registrationPayload,
        requestedReferences.length,
        unregisterMountedActions,
    ]);

    if (decisions.length === 0) return null;

    const visibilityPassed = decisions.every((action) => action.visible);
    const accessibilityPassed = decisions.every(hasPassedAccessibility);
    // Нажатие не доходит до пункта меню или ссылки, в которых индикатор стоит
    const activate = (event: MouseEvent<HTMLButtonElement>, stage: IndicatorStage) => {
        event.preventDefault();
        event.stopPropagation();
        const decision =
            decisions.find((action) => (stage === 'visibility' ? !action.visible : !hasPassedAccessibility(action))) ??
            decisions[0];
        selectAction(decision);
    };
    // Enter и пробел нажимают индикатор, а не пункт меню вокруг него
    const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
    };

    return (
        <span className="rf-action-diagnostics" data-testid="action-diagnostic-indicators">
            <button
                type="button"
                className="rf-action-diagnostics__indicator"
                data-result={visibilityPassed ? 'passed' : 'failed'}
                aria-label={`Visibility: ${visibilityPassed ? 'пройдено' : 'не пройдено'}. Открыть диагностику`}
                title={`Visibility: ${visibilityPassed ? 'пройдено' : 'не пройдено'}`}
                onClick={(event) => activate(event, 'visibility')}
                onKeyDown={onKeyDown}
            >
                {visibilityPassed ? <MdVisibility aria-hidden /> : <MdVisibilityOff aria-hidden />}
            </button>
            <button
                type="button"
                className="rf-action-diagnostics__indicator"
                data-result={accessibilityPassed ? 'passed' : 'failed'}
                aria-label={`Accessibility: ${accessibilityPassed ? 'пройдено' : 'не пройдено'}. Открыть диагностику`}
                title={`Accessibility: ${accessibilityPassed ? 'пройдено' : 'не пройдено'}`}
                onClick={(event) => activate(event, 'accessibility')}
                onKeyDown={onKeyDown}
            >
                {accessibilityPassed ? <MdShield aria-hidden /> : <MdWarningAmber aria-hidden />}
            </button>
        </span>
    );
};

export default ActionDiagnosticIndicators;
